(() => {
  'use strict';

  // https://help.orbe.app/developer-reference/javascript
  // Multi-store selection must stay inside Orbe's own popup.
  const MODAL_ID = 'md-app-embed__modal';
  const OPEN_CLASS = 'orbe--opened';

  function waitFor(read, { timeout = 8000, interval = 50, signal } = {}) {
    return new Promise((resolve, reject) => {
      const started = Date.now();
      let timer;
      const finish = (error, value) => {
        clearTimeout(timer);
        signal?.removeEventListener('abort', abort);
        if (error) reject(error);
        else resolve(value);
      };
      const abort = () => finish(new DOMException('Cancelled', 'AbortError'));
      const check = () => {
        if (signal?.aborted) { abort(); return; }
        try {
          const value = read();
          if (value) { finish(null, value); return; }
          if (Date.now() - started >= timeout) { finish(new Error('Orbe is unavailable')); return; }
          timer = setTimeout(check, interval);
        } catch (error) { finish(error); }
      };
      signal?.addEventListener('abort', abort, { once: true });
      check();
    });
  }

  function createController(win, doc, { timeout = 8000, interval = 50 } = {}) {
    const fallback = doc.getElementById('CountryLanguageSelector');
    if (!fallback) return null;
    const status = doc.querySelector('[data-country-language-status]');
    const triggers = () => doc.querySelectorAll('[data-country-language-trigger], .country-language-native-button');
    let pending;
    let opener;
    let returnFocus;
    let nativeContinuation;
    let nativeOpener = false;
    let nativeObserver;
    let nativeRefreshing = false;
    let view;
    let enhancing;
    let enhanced = false;
    let suspended = false;
    let destroyed = false;
    let wasOpen = doc.body.classList.contains(OPEN_CLASS);
    const popupOpen = () => doc.body.classList.contains(OPEN_CLASS);
    const historyHidden = () => doc.getElementById(MODAL_ID)?.hasAttribute?.('data-country-language-history-hidden');
    const closeNativePopup = () => {
      if (!popupOpen()) return;
      try {
        const api = typeof orbito === 'undefined' ? win.orbito : orbito;
        api?.closeModal?.(MODAL_ID);
      } catch { /* Keep the restored popup hidden if the app is unavailable. */ }
    };
    const nativeSlots = () => Array.from(doc.querySelectorAll('[data-country-language-native-slot]'))
      .filter((slot) => slot.dataset?.countryLanguageNativeSlot !== undefined);
    const refreshNativeLabels = () => {
      if (nativeRefreshing) return;
      nativeRefreshing = true;
      Promise.resolve(win.XteinkCountryLanguageNavigation?.refreshLabels()).catch(() => {}).then(() => {
        if (suspended || destroyed) return;
        for (const slot of nativeSlots()) {
          const fallbackLabel = slot.querySelector('[data-country-language-trigger] [data-country-language-label]');
          for (const trigger of slot.querySelectorAll('.country-language-native-button')) {
            const label = trigger.querySelector('[data-country-language-label]');
            if (label && fallbackLabel && label.textContent !== fallbackLabel.textContent) label.textContent = fallbackLabel.textContent;
            trigger.setAttribute('aria-haspopup', 'dialog');
            trigger.setAttribute('aria-controls', MODAL_ID);
            trigger.setAttribute('aria-expanded', String(popupOpen() && !historyHidden()));
            if (label) trigger.setAttribute('aria-label', label.textContent.trim());
            trigger.setAttribute('data-country-language-native-ready', 'true');
          }
        }
      }).finally(() => { nativeRefreshing = false; });
    };
    const bindNativeSlots = () => {
      const slots = nativeSlots();
      if (!slots.length) return;
      nativeObserver ||= new win.MutationObserver(refreshNativeLabels);
      slots.forEach((slot) => nativeObserver.observe(slot, { childList: true, subtree: true }));
      refreshNativeLabels();
    };
    const sync = () => {
      if (suspended || destroyed) return;
      bindNativeSlots();
      const isOpen = popupOpen() && !historyHidden();
      if (historyHidden()) closeNativePopup();
      triggers().forEach((trigger) => trigger.setAttribute('aria-expanded', String(isOpen)));
      if (wasOpen && !isOpen) {
        pending?.abort();
        enhancing?.abort();
        enhanced = false;
        view?.destroy();
        view = null;
        if (returnFocus?.isConnected) returnFocus.focus();
      }
      wasOpen = isOpen;
      if (isOpen && !pending && !enhanced && win.XteinkCountryLanguageView) {
        enhanced = true;
        enhancing = new AbortController();
        const currentEnhancement = enhancing;
        const signal = enhancing.signal;
        Promise.resolve().then(() => win.XteinkCountryLanguageView.prepare(doc, fallback.dataset, signal, { recommendation: true })).then((prepared) => {
          if (signal.aborted || suspended || destroyed || !popupOpen()) prepared?.destroy();
          else view = prepared;
        }).catch(() => {}).finally(() => { if (enhancing === currentEnhancement) enhancing = null; });
      }
    };
    const observer = new win.MutationObserver(sync);
    observer.observe(doc.body, { attributes: true, attributeFilter: ['class'] });

    async function open(trigger, event, nativeOpen) {
      if (suspended || destroyed || pending || (popupOpen() && !historyHidden())) return;
      opener = trigger;
      nativeOpener = Boolean(nativeOpen);
      returnFocus = trigger;
      const operation = new AbortController();
      pending = operation;
      const signal = operation.signal;
      status.textContent = fallback.dataset.loadingText;
      triggers().forEach((button) => button.setAttribute('aria-busy', 'true'));
      let failed = false;
      try {
        const drawer = trigger.closest('header-drawer');
        const details = drawer?.mainDetailsToggle;
        if (details?.open && typeof drawer.closeMenuDrawer === 'function') {
          returnFocus = details.querySelector('summary');
          drawer.closeMenuDrawer(event, returnFocus);
          returnFocus.setAttribute('aria-expanded', 'false');
          // Finish Dawn's close animation before Orbe takes focus.
          await waitFor(() => !details.open, { timeout: 1000, interval, signal });
        }
        const api = await waitFor(() => {
          // Orbe may expose a global lexical binding rather than a window property.
          const orbe = typeof orbito === 'undefined' ? win.orbito : orbito;
          return typeof orbe?.openModalWithoutRefresh === 'function' && doc.getElementById(MODAL_ID) && orbe;
        }, { timeout, interval, signal });
        if (signal.aborted) throw new DOMException('Cancelled', 'AbortError');
        if (historyHidden()) {
          // A restored page may still be finishing Orbe's closing animation.
          await waitFor(() => !popupOpen(), { timeout: Math.min(timeout, 1000), interval, signal });
          if (signal.aborted) throw new DOMException('Cancelled', 'AbortError');
          doc.getElementById(MODAL_ID)?.removeAttribute('data-country-language-history-hidden');
        }
        if (fallback.open) fallback.close();
        // Preserve storefront selections instead of replacing them with IP/browser defaults.
        if (nativeOpen) nativeOpen();
        else api.openModalWithoutRefresh();
        await waitFor(popupOpen, { timeout: Math.min(timeout, 2000), interval, signal });
        // The same view is used for manual and automatic Orbe openings.
        if (win.XteinkCountryLanguageView) {
          enhanced = true;
          try {
            const prepared = await win.XteinkCountryLanguageView.prepare(doc, fallback.dataset, signal);
            if (signal.aborted || suspended || destroyed || !popupOpen()) prepared?.destroy();
            else if (prepared) view = prepared;
            else win.XteinkCountryLanguageView.recoverNative?.(doc, fallback.dataset);
          } catch {
            if (!signal.aborted && !suspended && !destroyed && popupOpen()) {
              win.XteinkCountryLanguageView.recoverNative?.(doc, fallback.dataset);
            }
          }
        }
      } catch (error) {
        if (!signal.aborted && !suspended && !destroyed && error.name !== 'AbortError') {
          failed = true;
          if (typeof fallback.showModal === 'function' && !fallback.open) fallback.showModal();
          status.textContent = fallback.dataset.errorText;
        }
      } finally {
        // A cancelled operation may settle after a restored page starts another opening.
        if (pending === operation) {
          pending = null;
          triggers().forEach((button) => button.removeAttribute('aria-busy'));
          if (!failed) status.textContent = '';
          sync();
        }
      }
    }

    function beforeNativeOpen(trigger) {
      if (nativeContinuation === trigger) return true;
      if (!trigger || suspended || destroyed || pending || (popupOpen() && !historyHidden())) return false;
      void open(trigger, { type: 'click' }, () => {
        if (!trigger.isConnected) throw new Error('Native selector was replaced');
        // Resume the app block's own onclick once readiness and drawer closing finish.
        nativeContinuation = trigger;
        try { trigger.click(); } finally { nativeContinuation = null; }
      });
      return false;
    }

    const onClick = (event) => {
      const trigger = event.target.closest('[data-country-language-trigger]');
      if (trigger) { event.preventDefault(); void open(trigger, event); return; }
      if (event.target.closest('[data-country-language-close]')) { fallback.close(); return; }
      if (event.target.closest('[data-country-language-retry]') && opener) {
        fallback.close();
        if (nativeOpener) beforeNativeOpen(opener);
        else void open(opener, event);
      }
    };
    const onClose = () => { status.textContent = ''; if (!suspended && !destroyed && !pending && returnFocus?.isConnected) returnFocus.focus(); };
    const onPageHide = () => {
      suspended = true;
      // Keep the native IP recommendation hidden even in the first restored frame.
      doc.getElementById(MODAL_ID)?.setAttribute?.('data-country-language-history-hidden', '');
      pending?.abort();
      pending = null;
      enhancing?.abort();
      enhancing = null;
      view?.destroy(); // Cancels the pending selection's watchdog before entering bfcache.
      view = null;
      enhanced = false;
      wasOpen = false;
      closeNativePopup();
      if (fallback.open) fallback.close();
      returnFocus = null;
      status.textContent = '';
      triggers().forEach((trigger) => {
        trigger.removeAttribute('aria-busy');
        trigger.setAttribute('aria-expanded', 'false');
      });
      win.XteinkCountryLanguageNavigation?.cancel();
    };
    const onPageShow = (event) => {
      if (!event.persisted || destroyed) return;
      // History restores DOM and JS state; it must not restart the first-visit recommendation.
      closeNativePopup();
      suspended = false;
      sync();
      Promise.resolve(win.XteinkCountryLanguageNavigation?.refreshLabels()).catch(() => {});
    };
    doc.addEventListener('click', onClick);
    doc.addEventListener('shopify:section:load', sync);
    fallback.addEventListener('close', onClose);
    win.addEventListener('pagehide', onPageHide);
    win.addEventListener('pageshow', onPageShow);
    sync();
    return {
      open,
      beforeNativeOpen,
      destroy() {
        destroyed = true;
        onPageHide();
        enhancing?.abort();
        observer.disconnect();
        nativeObserver?.disconnect();
        view?.destroy();
        doc.removeEventListener('click', onClick);
        doc.removeEventListener('shopify:section:load', sync);
        fallback.removeEventListener('close', onClose);
        win.removeEventListener('pagehide', onPageHide);
        win.removeEventListener('pageshow', onPageShow);
      },
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { waitFor, createController };
  if (typeof window !== 'undefined') window.XteinkCountryLanguageSelector = createController(window, document);
})();
